import { ref } from 'vue'

import { useToast } from '@/uiKit/Toast/useToast'
import { useTeamStore } from '@/pinia/useTeamStore'
import type { TeamProperty } from '@/store/types/PropertyTypes'
import type {
  DatasetItemPropertiesResponse,
  DatasetItemPropertyActor,
  DatasetItemPropertyId,
  DatasetItemPropertyValues,
} from '@/backend/darwin/teamProperties'
import { getTeamProperties, reorderTeamProperties } from '@/backend/darwin/teamProperties'
import { defineStore } from 'pinia'
import { useUserStore } from '@/modules/Auth/useUserStore'
import { useMembershipStore } from '@/modules/Teams/useMembershipStore'

export const CREATE_PROPERTY_VALUE_KEY = 'create_value'

export const isTemporaryPropertyValueId = (id: string): boolean =>
  id.startsWith(CREATE_PROPERTY_VALUE_KEY)

/**
 * Store used to fetch all team properties and keep the loading state in sync.
 * This is also responsible for holding current item selected property values
 */
export const useTeamProperties = defineStore('properties', () => {
  const teamStore = useTeamStore()
  const membershipStore = useMembershipStore()
  const userStore = useUserStore()
  const toast = useToast()

  const properties = ref<TeamProperty[]>([])
  const loaded = ref(false)

  const setProperties = (payload: TeamProperty[]): void => {
    properties.value = Object.seal(payload)
  }

  const fetchProperties = async (teamSlug: string): Promise<void> => {
    loaded.value = false
    const response = await getTeamProperties(teamSlug)

    if ('error' in response) {
      const message =
        typeof response.error.message === 'string' ? response.error.message : 'Something went wrong'

      toast.error({ meta: { title: message } })
      loaded.value = true
    } else {
      setProperties(response.data.properties)
      loaded.value = true
    }
  }

  const reorderProperties = async (
    teamSlug: string,
    classId: string | number,
    propertyIds: string[],
  ): Promise<void> => {
    propertyIds.forEach((id, index) => {
      const property = properties.value.find((p) => p.id === id)
      if (property) {
        property.position = index
      }
    })
    const result = await reorderTeamProperties(teamSlug, classId, propertyIds)
    if ('error' in result) {
      const message =
        typeof result.error.message === 'string' ? result.error.message : 'Failed to reorder properties'
      toast.error({ meta: { title: message } })
      return
    }

    // Properties are sealed and non reactive - let's update the position and re-seal the array
    const updatedProperties = properties.value.map((property) => {
      if (property.annotation_class_id !== classId) {
        return property
      }
      return {
        ...property,
        position: propertyIds.indexOf(property.id),
      }
    })
    properties.value = Object.seal(updatedProperties)
  }

  const getCurrentActor = (): DatasetItemPropertyActor | null => {
    const currentTeamId = teamStore.currentTeam?.id
    if (!currentTeamId || !userStore.currentUserId) {
      return null
    }
    const member = membershipStore.getMembershipByTeamIdUserId(
      currentTeamId,
      userStore.currentUserId,
    )
    if (member) {
      return {
        user_id: member.user_id,
        role: member.role,
        model_id: undefined,
      }
    }
    return null
  }

  const currentItemProperties = ref<DatasetItemPropertiesResponse['properties']>([])
  const setCurrentItemPropertyValues = (
    propertyId: DatasetItemPropertyId,
    values: DatasetItemPropertyValues,
  ): void => {
    currentItemProperties.value = currentItemProperties.value.filter(
      (itemProperty) => itemProperty.id !== propertyId,
    )
    const member = getCurrentActor()
    currentItemProperties.value.push({
      id: propertyId,
      values,
      actors: member ? [member] : [],
    })
  }

  return {
    properties,
    reorderProperties,
    setProperties,
    fetchProperties,
    loaded,
    currentItemProperties,
    setCurrentItemPropertyValues,
  }
})
